// M1.6 watcher admin — manual "Run now" button per registered watcher.
// In production, the same handlers fire on Cloudflare Cron Triggers. This
// surface lets us test + force-run watchers without waiting for the schedule.

import { useState } from 'react';
import { Brain } from '@/sdk/brain';
import type { WatcherRunResult } from '@/sdk/brain';

interface WatcherDef {
  id: string;
  name: string;
  description: string;
  schedule: string;
}

const REGISTERED: WatcherDef[] = [
  {
    id: 'production_health_check',
    name: 'Production health check',
    description:
      'Runs a synthetic RFQ through extract.rfq_from_email end-to-end. Flags if extraction fails or latency spikes.',
    schedule: 'every 4 hours (when Worker deployed)',
  },
];

interface RunState {
  result?: WatcherRunResult;
  error?: string;
  running?: boolean;
}

export function WatcherAdmin() {
  const [runs, setRuns] = useState<Record<string, RunState>>({});

  async function runWatcher(id: string) {
    setRuns((prev) => ({ ...prev, [id]: { running: true } }));
    try {
      const result = await Brain.watchers.run(id);
      setRuns((prev) => ({ ...prev, [id]: { result, running: false } }));
    } catch (e) {
      setRuns((prev) => ({
        ...prev,
        [id]: { error: (e as Error).message, running: false },
      }));
    }
  }

  return (
    <div className="bg-paper border border-line rounded p-4">
      <h2 className="font-semibold text-navy mb-1">Watchers (M1.6)</h2>
      <p className="text-xs text-text-mute mb-4">
        Background checks that monitor production health, stale quotes, AR aging, etc. Click <strong>Run now</strong>{' '}
        to invoke a watcher manually. If it fails, a flag appears in the 🔔 dropdown in the header.
      </p>

      <ul className="space-y-3">
        {REGISTERED.map((w) => {
          const state = runs[w.id] ?? {};
          return (
            <li key={w.id} className="border border-line rounded p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{w.name}</div>
                  <div className="text-xs text-text-mute mt-0.5">
                    <code className="bg-bg px-1 rounded">{w.id}</code> · {w.schedule}
                  </div>
                  <div className="text-xs mt-2">{w.description}</div>
                </div>
                <button
                  onClick={() => runWatcher(w.id)}
                  disabled={state.running}
                  className="px-3 py-1 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50 whitespace-nowrap"
                >
                  {state.running ? 'Running…' : 'Run now'}
                </button>
              </div>

              {state.result && (
                <div
                  className={`mt-3 border-l-4 px-3 py-2 text-sm ${
                    state.result.ok ? 'border-green bg-green/5' : 'border-orange bg-orange/5'
                  }`}
                >
                  <div className="font-semibold">
                    {state.result.ok ? '✓ OK' : '✗ FAILED'} · {state.result.durationMs}ms
                  </div>
                  <div className="text-xs mt-1 text-text-mute">{state.result.details}</div>
                  {state.result.raiseFlag && (
                    <div className="text-xs mt-2 italic">
                      Raised flag: {state.result.raiseFlag.title}
                    </div>
                  )}
                </div>
              )}

              {state.error && (
                <div className="mt-3 bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm">
                  ⚠ {state.error}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
